<template>
  <div
    class="mt-4 ai-border"
  >
    <div class="flex items-center justify-between px-6 py-3 border-b border-gray-300">
      <h3 class="text-xl font-bold text-blue-400 m-0">{{ title }}</h3>
      <button
        class="hover:text-black leading-none bg-transparent border-none cursor-pointer p-0"
        @click="$emit('close')"
        aria-label="Close"
      >
        <span class="material-icons-round" aria-hidden="true">close</span>
      </button>
    </div>

    <div class="p-3 flex flex-col gap-2">
      <GbInlineMessage gbTheme="red" class="max-w-2xl" v-if="error">
        <template #icon></template>
        <div class="inline-flex items-center">
          <span class="material-icons-round text-red-300 mr-2 self-start">warning_amber</span>
          <p class="text-gray-900">{{ error }}</p>
        </div>
      </GbInlineMessage>

      <template v-else-if="isLoading">
        <div class="flex items-center gap-4">
          <ProgressSpinner class="h-8 w-8 stroke-gray-700 !mx-0 grow-0" strokeWidth="4" />
          <p class="text-gray-700">Reviewing Impact Statement...</p>
        </div>
      </template>
      <template v-else>
        <div
          v-for="result in results"
          :key="result.audit_title"
          class="border border-gray-400 rounded-md overflow-hidden"
        >
          <button
            class="w-full flex items-center gap-4 px-4 py-4 text-left hover:bg-gray-100 bg-white border-none cursor-pointer"
            data-testid="expand-button"
            @click="handleToggle(result)"
          >
            <span
              class="text-md font-bold text-white px-2 py-1 rounded-xs text-center min-w-[53px]"
              :class="scoreBadgeClass(result.score, result.max_score)"
            >
              {{ result.score }}/{{ result.max_score }}
            </span>
            <span class="flex-1 text-lg font-medium text-gray-900">{{ result.audit_title }}</span>
            <span class="material-icons-round text-gray-700" aria-hidden="true">
              {{ expandedItems.has(result.audit_title) ? 'expand_less' : 'expand_more' }}
            </span>
          </button>
          <div
            v-show="expandedItems.has(result.audit_title)"
            data-testid="expanded-content"
            class="px-4 pb-4 pt-3 border-t border-gray-300 text-md space-y-3"
          >
            <div>
              <h4 class="font-medium text-md mb-1">Rationale</h4>
              <p class="whitespace-pre-wrap font-normal">{{ result.score_rationale }}</p>
            </div>
            <div v-if="result.suggestions">
              <h4 class="font-medium text-md mb-1">Suggestions for Improvement</h4>
              <p class="whitespace-pre-wrap font-normal">{{ result.suggestions }}</p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import ProgressSpinner from 'primevue/progressspinner'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import type { AuditResult } from '@/content-review/api'

const props = withDefaults(defineProps<{
  results: AuditResult[]
  isLoading: boolean
  error: string | null
  expectedCount: number
  title?: string
}>(), {
  title: 'Review Results',
})

const emit = defineEmits<{
  close: []
  toggle: [result: AuditResult, expanded: boolean]
}>()

const expandedItems = ref(new Set<string>())

function handleToggle(result: AuditResult) {
  const wasExpanded = expandedItems.value.has(result.audit_title)
  if (wasExpanded) {
    expandedItems.value.delete(result.audit_title)
  } else {
    expandedItems.value.add(result.audit_title)
  }
  emit('toggle', result, !wasExpanded)
}

function scoreBadgeClass(score: number, maxScore: number): string {
  const pct = maxScore > 0 ? score / maxScore : 0
  if (pct >= 0.8) return 'bg-green-300'
  if (pct >= 0.5) return 'bg-orange-300'
  return 'bg-red-300'
}
</script>